import { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock, MapPin, User, Repeat, Edit, Trash2, X, Check, Printer, Mail, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { generateScheduleReportPDF } from '../utils/PDFGenerator';
import { useAuth } from '../contexts/AuthContext';
import inspectionsApi from '../services/inspectionsApi';
import CrewFilter from '../components/CrewFilter';
import useCloudSync from '../hooks/useCloudSync';
import {
  crewIdForEmployeeIds,
  getCrewColor,
  getCrewName,
  getEffectiveCrewFilter,
} from '../services/CrewService';
import { IconTile, TINT } from '../components/ui/IconTile';

const Scheduling = () => {
  const { user } = useAuth();
  // Employees see Scheduling in read-only mode: they can view (crew-
  // filtered) appointments but the "New Appointment" button, inline
  // edit-on-click, and delete affordances are hidden. Owners/managers
  // keep the full edit surface.
  const isEmployee = user?.role === 'employee';
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // week, day, month
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printFilter, setPrintFilter] = useState('all'); // all or specific employee ID
  const [printView, setPrintView] = useState('weekly'); // 'weekly' or 'daily'
  const [selectedPrintDate, setSelectedPrintDate] = useState(new Date().toISOString().split('T')[0]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailContent, setEmailContent] = useState({ subject: '', body: '' });
  // Crew filter — driven by the global CrewFilter dropdown. For employees,
  // this is locked to THEIR crew; for owners/managers it's their selection.
  const [crewFilter, setCrewFilter] = useState(getEffectiveCrewFilter(user));
  useEffect(() => {
    const onFilter = () => setCrewFilter(getEffectiveCrewFilter(user));
    window.addEventListener('maidly:crew-filter-changed', onFilter);
    return () => window.removeEventListener('maidly:crew-filter-changed', onFilter);
  }, [user]);
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    address: '',
    serviceType: 'standard',
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    duration: 2,
    assignedEmployees: [],
    notes: '',
    status: 'scheduled',
    recurring: false,
    recurringFrequency: 'weekly',
    recurringEndDate: ''
  });

  const serviceTypes = [
    { value: 'standard', label: 'Standard Cleaning', duration: 2, color: '#0073ea' },
    { value: 'deep', label: 'Deep Cleaning', duration: 4, color: '#1e3a5f' },
    { value: 'movein', label: 'Move-In/Move-Out', duration: 5, color: '#f5576c' },
    { value: 'post_construction', label: 'Post-Construction', duration: 6, color: '#fdab3d' },
    { value: 'carpet', label: 'Carpet Cleaning', duration: 3, color: '#00c875' }
  ];

  const recurringOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-Weekly (Every 2 weeks)' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly (Every 3 months)' }
  ];

  useEffect(() => {
    loadData();
    // Always snap the view back to "today's week" whenever the user re-enters
    // the Schedule & Dispatch tab. Without this, returning to a stale future
    // week after a deep-link or navigation can be disorienting.
    setCurrentDate(new Date());
  }, []);

  // Realtime: refresh when other devices push appointment/customer/employee changes
  useCloudSync(() => loadData(), ['appointments', 'customers', 'employees']);

  const loadData = () => {
    const appointmentsData = JSON.parse(localStorage.getItem('maidly_appointments') || '[]');
    setAppointments(appointmentsData);
    const customersData = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
    setCustomers(customersData);
    const employeesData = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    setEmployees(employeesData.filter(e => e.status === 'active'));
  };

  // Create inspection for an appointment
  const createInspectionForAppointment = async (appointment) => {
    try {
      const userId = user?.id || 'default-user';
      const today = new Date().toISOString().split('T')[0];
      
      // Only create inspection if appointment is for today or earlier
      if (appointment.date <= today) {
        await inspectionsApi.create({
          user_id: userId,
          appointment_id: appointment.id,
          customer_id: appointment.customerId,
          customer_name: appointment.customerName,
          address: appointment.address,
          service_type: appointment.serviceType,
          scheduled_date: appointment.date,
          assigned_employees: appointment.assignedEmployees,
          status: 'pending',
          checklist: {},
          overall_notes: '',
          overall_rating: null,
          photos: [],
          started_at: null,
          completed_at: null,
          approved_at: null,
          approved_by: null,
          customer_signature: null,
          inspector_signature: null,
          location: null
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error creating inspection:', error);
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.customerId) {
      toast.error('Please select a customer');
      return;
    }

    if (formData.assignedEmployees.length === 0) {
      toast.error('Please assign at least one employee');
      return;
    }

    if (formData.recurring && !formData.recurringEndDate) {
      toast.error('Please set an end date for recurring appointments');
      return;
    }

    if (editingAppointment) {
      const updated = appointments.map(apt => 
        apt.id === editingAppointment.id ? { ...apt, ...formData } : apt
      );
      setAppointments(updated);
      localStorage.setItem('maidly_appointments', JSON.stringify(updated));
      toast.success('Appointment updated successfully');
    } else {
      if (formData.recurring) {
        createRecurringAppointments();
      } else {
        createSingleAppointment();
      }
    }
    
    resetForm();
  };

  const createSingleAppointment = async () => {
    const customer = customers.find(c => c.id === formData.customerId);
    const newAppointment = {
      ...formData,
      id: Date.now().toString(),
      customerName: customer.name,
      address: customer.address,
      createdAt: new Date().toISOString(),
      recurringGroup: null
    };

    const updated = [...appointments, newAppointment];
    setAppointments(updated);
    localStorage.setItem('maidly_appointments', JSON.stringify(updated));
    
    // Create inspection for this appointment
    const inspectionCreated = await createInspectionForAppointment(newAppointment);
    
    if (inspectionCreated) {
      toast.success('Appointment created with inspection');
    } else {
      toast.success('Appointment scheduled');
    }
  };

  const createRecurringAppointments = async () => {
    const customer = customers.find(c => c.id === formData.customerId);
    const recurringGroupId = Date.now().toString();
    const newAppointments = [];
    
    let currentDate = new Date(formData.date);
    const endDate = new Date(formData.recurringEndDate);
    
    const incrementMap = {
      weekly: 7,
      biweekly: 14,
      monthly: 30,
      quarterly: 90
    };
    
    const increment = incrementMap[formData.recurringFrequency];
    let count = 0;
    const maxOccurrences = 52; // Safety limit
    
    while (currentDate <= endDate && count < maxOccurrences) {
      const appointment = {
        ...formData,
        id: `${recurringGroupId}-${count}`,
        date: currentDate.toISOString().split('T')[0],
        customerName: customer.name,
        address: customer.address,
        createdAt: new Date().toISOString(),
        recurringGroup: recurringGroupId
      };
      
      newAppointments.push(appointment);
      
      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + increment);
      count++;
    }

    const updated = [...appointments, ...newAppointments];
    setAppointments(updated);
    localStorage.setItem('maidly_appointments', JSON.stringify(updated));
    
    // Create inspections for appointments that are due today or earlier
    let inspectionsCreated = 0;
    for (const apt of newAppointments) {
      const created = await createInspectionForAppointment(apt);
      if (created) inspectionsCreated++;
    }
    
    if (inspectionsCreated > 0) {
      toast.success(`Created ${newAppointments.length} appointments (${inspectionsCreated} inspection(s) ready)`);
    } else {
      toast.success(`Created ${newAppointments.length} recurring appointments`);
    }
  };

  const handleDelete = (id) => {
    const appointment = appointments.find(a => a.id === id);
    
    if (appointment.recurringGroup) {
      if (window.confirm('Delete this appointment or all appointments in this series?\n\nOK = This only\nCancel = All in series')) {
        const updated = appointments.filter(apt => apt.id !== id);
        setAppointments(updated);
        localStorage.setItem('maidly_appointments', JSON.stringify(updated));
        toast.success('Appointment deleted');
      } else {
        const updated = appointments.filter(apt => apt.recurringGroup !== appointment.recurringGroup);
        setAppointments(updated);
        localStorage.setItem('maidly_appointments', JSON.stringify(updated));
        toast.success('Recurring series deleted');
      }
    } else {
      if (window.confirm('Are you sure you want to delete this appointment?')) {
        const updated = appointments.filter(apt => apt.id !== id);
        setAppointments(updated);
        localStorage.setItem('maidly_appointments', JSON.stringify(updated));
        toast.success('Appointment deleted');
      }
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      customerId: appointment.customerId,
      customerName: appointment.customerName,
      address: appointment.address,
      serviceType: appointment.serviceType,
      date: appointment.date,
      startTime: appointment.startTime,
      duration: appointment.duration,
      assignedEmployees: appointment.assignedEmployees,
      notes: appointment.notes,
      status: appointment.status,
      recurring: false,
      recurringFrequency: 'weekly',
      recurringEndDate: ''
    });
    setShowModal(true);
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customerId,
        customerName: customer.name,
        address: customer.address
      });
    }
  };

  const toggleEmployee = (employeeId) => {
    if (formData.assignedEmployees.includes(employeeId)) {
      setFormData({
        ...formData,
        assignedEmployees: formData.assignedEmployees.filter(id => id !== employeeId)
      });
    } else {
      setFormData({
        ...formData,
        assignedEmployees: [...formData.assignedEmployees, employeeId]
      });
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingAppointment(null);
    setFormData({
      customerId: '',
      customerName: '',
      address: '',
      serviceType: 'standard',
      date: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      duration: 2,
      assignedEmployees: [],
      notes: '',
      status: 'scheduled',
      recurring: false,
      recurringFrequency: 'weekly',
      recurringEndDate: ''
    });
  };

  const getWeekDays = () => {
    const week = [];
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments
      .filter(apt => apt.date === dateStr)
      .filter(apt => !crewFilter || crewIdForEmployeeIds(apt.assignedEmployees) === crewFilter)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const changeWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  // -------------------------------------------------------------------
  // Interactive helpers — drag & drop, inline edits, conflict detection
  // -------------------------------------------------------------------
  const persistAppointments = useCallback((next) => {
    setAppointments(next);
    localStorage.setItem('maidly_appointments', JSON.stringify(next));
  }, []);

  // Status cycle: scheduled -> in-progress -> completed -> cancelled -> scheduled
  const STATUS_ORDER = ['scheduled', 'in-progress', 'completed', 'cancelled'];
  const STATUS_STYLE = {
    'scheduled':   { bg: 'rgba(255,255,255,0.22)', label: 'Scheduled' },
    'in-progress': { bg: '#fde68a',                fg: '#7c2d12', label: 'In progress' },
    'completed':   { bg: '#bbf7d0',                fg: '#14532d', label: 'Completed' },
    'cancelled':   { bg: '#fecaca',                fg: '#7f1d1d', label: 'Cancelled' },
  };
  const cycleStatus = (apt, e) => {
    e.stopPropagation();
    const idx = STATUS_ORDER.indexOf(apt.status || 'scheduled');
    const nextStatus = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
    const next = appointments.map((a) => a.id === apt.id
      ? { ...a, status: nextStatus, updatedAt: new Date().toISOString() }
      : a);
    persistAppointments(next);
    toast.success(`Marked "${apt.customerName}" as ${STATUS_STYLE[nextStatus].label}`);
  };

  const quickReassign = (apt, employeeId, e) => {
    e.stopPropagation();
    if (!employeeId) return;
    // Toggle: if employeeId is present, remove; else replace assignments with just this one
    const has = apt.assignedEmployees.includes(employeeId);
    const newAssign = has
      ? apt.assignedEmployees.filter((id) => id !== employeeId)
      : [employeeId];
    const emp = employees.find((x) => x.id === employeeId);
    const next = appointments.map((a) => a.id === apt.id
      ? { ...a, assignedEmployees: newAssign, updatedAt: new Date().toISOString() }
      : a);
    persistAppointments(next);
    toast.success(has
      ? `Removed ${emp?.name || 'employee'} from "${apt.customerName}"`
      : `Reassigned "${apt.customerName}" to ${emp?.name || 'employee'}`);
  };

  // Hover state — the column under the mouse gets a blue border so the
  // user gets visual feedback that the whole column is clickable.
  const [hoveredDate, setHoveredDate] = useState(null);

  // Drag & drop — move an appointment to a different date.
  // After a drop we open a small reschedule popup so the user can confirm
  // (and optionally tweak the time) before the move is committed.
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetDate, setDropTargetDate] = useState(null);
  // { apt, dateStr, startTime } | null
  const [pendingMove, setPendingMove] = useState(null);
  const onDragStart = (apt, e) => {
    setDraggingId(apt.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', apt.id);
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setDropTargetDate(null);
  };
  const onDayDragOver = (dateStr, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetDate !== dateStr) setDropTargetDate(dateStr);
  };
  const onDayDrop = (dateStr, e) => {
    e.preventDefault();
    const aptId = e.dataTransfer.getData('text/plain') || draggingId;
    onDragEnd();
    if (!aptId) return;
    const moving = appointments.find((a) => a.id === aptId);
    if (!moving) return;
    // Open reschedule popup pre-filled with the dropped day + current time.
    setPendingMove({
      apt: moving,
      dateStr,
      startTime: moving.startTime || '09:00',
    });
  };

  const confirmPendingMove = () => {
    if (!pendingMove) return;
    const { apt, dateStr, startTime } = pendingMove;
    const next = appointments.map((a) => a.id === apt.id
      ? { ...a, date: dateStr, startTime, updatedAt: new Date().toISOString() }
      : a);
    persistAppointments(next);
    toast.success(`Rescheduled "${apt.customerName}" to ${new Date(dateStr).toLocaleDateString()} ${startTime}`);
    setPendingMove(null);
  };

  // Click-to-create — open the new-appt modal pre-filled with day + 9am.
  // Build the YYYY-MM-DD string from local Y/M/D parts (NOT toISOString())
  // so users in negative-UTC timezones don't see the modal land on the
  // previous day.
  const localDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const openCreateForDay = (date) => {
    setEditingAppointment(null);
    setFormData((prev) => ({
      ...prev,
      customerId: '',
      customerName: '',
      address: '',
      date: localDateStr(date),
      startTime: '09:00',
      assignedEmployees: [],
      notes: '',
      status: 'scheduled',
      recurring: false,
    }));
    setShowModal(true);
  };

  // Conflict detection — two appointments conflict when they share at least
  // one assigned employee AND their [start, end) windows overlap on the
  // same date. We compute it once per appointments-change for the visible
  // week. Each conflict is reported once per appointment (so an A↔B pair
  // shows up twice — UI just needs "is this card conflicted?").
  const conflictIdSet = useMemo(() => {
    const flagged = new Set();
    const minutes = (hhmm) => {
      const [h, m] = (hhmm || '00:00').split(':').map(Number);
      return h * 60 + (m || 0);
    };
    const byDay = new Map();
    for (const apt of appointments) {
      if (!byDay.has(apt.date)) byDay.set(apt.date, []);
      byDay.get(apt.date).push(apt);
    }
    for (const dayList of byDay.values()) {
      for (let i = 0; i < dayList.length; i++) {
        const a = dayList[i];
        const aStart = minutes(a.startTime);
        const aEnd = aStart + (Number(a.duration) || 0) * 60;
        for (let j = i + 1; j < dayList.length; j++) {
          const b = dayList[j];
          const bStart = minutes(b.startTime);
          const bEnd = bStart + (Number(b.duration) || 0) * 60;
          const overlap = aStart < bEnd && bStart < aEnd;
          if (!overlap) continue;
          const sharedEmployee = (a.assignedEmployees || []).some((id) => (b.assignedEmployees || []).includes(id));
          if (!sharedEmployee) continue;
          flagged.add(a.id);
          flagged.add(b.id);
        }
      }
    }
    return flagged;
  }, [appointments]);

  // Count conflicts visible in the current week so we can show a top-bar pill.
  const weekConflictCount = useMemo(() => {
    const inWeek = new Set(getWeekDays().map((d) => localDateStr(d)));
    let count = 0;
    for (const id of conflictIdSet) {
      const apt = appointments.find((a) => a.id === id);
      if (apt && inWeek.has(apt.date)) count++;
    }
    return count;
    // getWeekDays + localDateStr are pure and stable; recompute when the week
    // pointer or the conflict set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflictIdSet, appointments, currentDate]);

  const getFilteredAppointments = () => {
    if (printFilter === 'all') return appointments;
    return appointments.filter(apt => apt.assignedEmployees.includes(printFilter));
  };

  // Sunday-anchored start of the week containing `date`. Used by print/email
  // to compute a full 7-day range. NOTE: was previously called but never
  // defined, which threw ReferenceError when users clicked "Print Schedule"
  // on Weekly view.
  const getWeekStart = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay());
    return d;
  };

  const handlePrint = () => {
    // Generate PDF instead of browser print
    const filteredAppts = getFilteredAppointments();
    
    if (filteredAppts.length === 0) {
      toast.error('No appointments to export');
      return;
    }

    // Get date range
    let startDate, endDate;
    if (printView === 'daily') {
      startDate = selectedPrintDate;
      endDate = selectedPrintDate;
    } else {
      const start = getWeekStart(currentDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      startDate = start.toISOString().split('T')[0];
      endDate = end.toISOString().split('T')[0];
    }

    generateScheduleReportPDF(filteredAppts, { start: startDate, end: endDate });
    toast.success('PDF Schedule downloaded!');
  };

  const handleEmail = () => {
    try {
      let emailBody = '';
      let subject = '';
      const filteredAppts = getFilteredAppointments();
      
      // Check if there are any appointments
      if (filteredAppts.length === 0) {
        toast.error('No appointments to email');
        return;
      }
      
      if (printView === 'daily') {
        // Daily schedule email
        const selectedDate = new Date(selectedPrintDate);
        const dateStr = selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        
        emailBody = `Daily Schedule: ${dateStr}\n\n`;
        
        if (printFilter !== 'all') {
          const employee = employees.find(e => e.id === printFilter);
          emailBody += `Employee: ${employee?.name || 'Unknown'}\n\n`;
        }
        
        const dayAppts = filteredAppts.filter(apt => apt.date === selectedPrintDate);
        
        if (dayAppts.length === 0) {
          emailBody += 'No appointments scheduled for this day.\n';
        } else {
          dayAppts.sort((a, b) => a.startTime.localeCompare(b.startTime)).forEach(apt => {
            const service = serviceTypes.find(s => s.value === apt.serviceType);
            emailBody += `• ${apt.startTime} - ${apt.customerName} - ${service?.label} (${apt.duration}hrs)\n`;
            emailBody += `  ${apt.address}\n`;
            const assignedNames = employees.filter(e => apt.assignedEmployees.includes(e.id)).map(e => e.name).join(', ');
            emailBody += `  Assigned: ${assignedNames}\n`;
            if (apt.notes) {
              emailBody += `  Notes: ${apt.notes}\n`;
            }
            emailBody += `\n`;
          });
        }
        
        subject = printFilter === 'all' 
          ? `Daily Schedule: ${dateStr}`
          : `${employees.find(e => e.id === printFilter)?.name}'s Schedule: ${dateStr}`;
          
      } else {
        // Weekly schedule email
        const weekStart = weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        const weekEnd = weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        
        emailBody = `Weekly Schedule: ${weekStart} - ${weekEnd}\n\n`;
        
        if (printFilter !== 'all') {
          const employee = employees.find(e => e.id === printFilter);
          emailBody += `Employee: ${employee?.name || 'Unknown'}\n\n`;
        }
        
        weekDays.forEach(day => {
          const dayAppts = filteredAppts.filter(apt => apt.date === day.toISOString().split('T')[0]);
          if (dayAppts.length > 0) {
            emailBody += `${day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}\n`;
            dayAppts.sort((a, b) => a.startTime.localeCompare(b.startTime)).forEach(apt => {
              const service = serviceTypes.find(s => s.value === apt.serviceType);
              emailBody += `  • ${apt.startTime} - ${apt.customerName} - ${service?.label} (${apt.duration}hrs)\n`;
              emailBody += `    ${apt.address}\n`;
              const assignedNames = employees.filter(e => apt.assignedEmployees.includes(e.id)).map(e => e.name).join(', ');
              emailBody += `    Assigned: ${assignedNames}\n`;
              if (apt.notes) {
                emailBody += `    Notes: ${apt.notes}\n`;
              }
            });
            emailBody += '\n';
          }
        });
        
        subject = printFilter === 'all' 
          ? `Weekly Schedule: ${weekStart} - ${weekEnd}`
          : `${employees.find(e => e.id === printFilter)?.name}'s Schedule: ${weekStart} - ${weekEnd}`;
      }
      
      // Store email content and show modal
      setEmailContent({ subject, body: emailBody });
      setShowEmailModal(true);
      
    } catch (error) {
      console.error('Error creating email:', error);
      toast.error('Failed to prepare email content.');
    }
  };

  const copyEmailContent = () => {
    const fullContent = `Subject: ${emailContent.subject}\n\n${emailContent.body}`;
    navigator.clipboard.writeText(fullContent).then(() => {
      toast.success('Email content copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const openInEmailClient = () => {
    try {
      const mailtoLink = `mailto:?subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(emailContent.body)}`;
      
      // Create a temporary anchor element
      const anchor = document.createElement('a');
      anchor.href = mailtoLink;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      
      // Click the anchor
      anchor.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(anchor);
      }, 100);
      
      toast.success('Opening email client...');
    } catch (error) {
      console.error('Error opening email client:', error);
      toast.error('Failed to open email client. Please use "Copy to Clipboard" instead.');
    }
  };

  const weekDays = getWeekDays();
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div data-testid="scheduling-page" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IconTile icon={CalendarIcon} tint={TINT.scheduling} size={36} />
            Schedule &amp; Dispatch
          </h1>
          <p style={{ color: '#6b7280' }}>Manage recurring home cleaning jobs</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <CrewFilter />

          {/* Print/Email Schedule — sky-blue outline (matches Mileage tint) */}
          <button
            onClick={() => setShowPrintModal(true)}
            data-testid="print-schedule-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#0ea5e90d';
              e.currentTarget.style.borderColor = '#0ea5e966';
              e.currentTarget.style.color = TINT.mileage;
              e.currentTarget.style.boxShadow = '0 6px 16px #0ea5e933';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Printer} tint={TINT.mileage} size={30} />
            Print/Email Schedule
          </button>

          {/* New Appointment — orange primary (matches Scheduling sidebar tint) */}
          {!isEmployee && (
          <button
            onClick={() => setShowModal(true)}
            data-testid="new-appointment-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem 0.55rem 0.55rem',
              background: TINT.scheduling, color: '#fff',
              border: 'none', borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 700,
              boxShadow: `0 6px 16px ${TINT.scheduling}55`,
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              letterSpacing: 0.1,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#ea580c';
              e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.scheduling}88`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = TINT.scheduling;
              e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.scheduling}55`;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
            New Appointment
          </button>
          )}
        </div>
      </div>

      {/* Calendar Controls */}
      <div className="card" style={{ marginBottom: '2rem', padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => changeWeek(-1)} className="btn btn-secondary" style={{ padding: '0.5rem' }}>
              <ChevronLeft size={20} />
            </button>
            <div style={{ fontSize: '1.125rem', fontWeight: 600, minWidth: '250px', textAlign: 'center' }}>
              {weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <button onClick={() => changeWeek(1)} className="btn btn-secondary" style={{ padding: '0.5rem' }}>
              <ChevronRight size={20} />
            </button>
            <button onClick={() => setCurrentDate(new Date())} className="btn btn-secondary">
              Today
            </button>
          </div>
          {weekConflictCount > 0 && (
            <div
              data-testid="conflict-counter"
              title="Click an appointment marked with the warning icon to resolve"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '0.4rem 0.75rem', borderRadius: 999,
                background: '#fef2f2', color: '#b91c1c',
                border: '1px solid #fecaca',
                fontSize: '0.85rem', fontWeight: 600,
              }}
            >
              <AlertTriangle size={14} />
              {weekConflictCount} conflict{weekConflictCount === 1 ? '' : 's'} this week
            </div>
          )}
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            Tip: drag a card to a new day to reschedule · click the status pill to advance it
          </div>
        </div>
      </div>

      {/* Weekly Calendar Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
        {weekDays.map((day) => {
          const dayAppointments = getAppointmentsForDate(day);
          const dateStr = localDateStr(day);
          const isToday = dateStr === todayStr;
          const isDropTarget = dropTargetDate === dateStr;
          // Only one column is highlighted at a time:
          // - if the mouse is over any column → that one wins
          // - otherwise → today's column is highlighted by default
          const isHighlighted = hoveredDate
            ? hoveredDate === dateStr
            : isToday;
          
          return (
            <div
              key={dateStr}
              className="card"
              data-testid={`schedule-day-${dateStr}`}
              role="button"
              tabIndex={0}
              aria-label={`Add appointment on ${day.toLocaleDateString()}`}
              onClick={() => openCreateForDay(day)}
              onMouseEnter={() => setHoveredDate(dateStr)}
              onMouseLeave={() => setHoveredDate((prev) => (prev === dateStr ? null : prev))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCreateForDay(day);
                }
              }}
              onDragOver={(e) => onDayDragOver(dateStr, e)}
              onDragLeave={() => dropTargetDate === dateStr && setDropTargetDate(null)}
              onDrop={(e) => onDayDrop(dateStr, e)}
              style={{
                padding: '1rem',
                minHeight: '400px',
                background: isDropTarget ? '#dbeafe' : (isHighlighted ? '#f0f9ff' : '#fff'),
                border: isDropTarget
                  ? '2px dashed #2563eb'
                  : (isHighlighted ? '2px solid #0073ea' : '1px solid #e5e7eb'),
                transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                cursor: 'pointer',
                boxShadow: isHighlighted && !isDropTarget ? '0 4px 14px rgba(0,115,234,0.12)' : 'none',
              }}
            >
              <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>
                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  marginTop: '0.25rem'
                }}>
                  {day.getDate()}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {dayAppointments.length > 0 && (
                  <>
                    {dayAppointments.map(apt => {
                      const service = serviceTypes.find(s => s.value === apt.serviceType);
                      const aptCrewId = crewIdForEmployeeIds(apt.assignedEmployees);
                      const crewColor = aptCrewId ? getCrewColor(aptCrewId) : null;
                      const status = apt.status || 'scheduled';
                      const statusStyle = STATUS_STYLE[status];
                      const isConflict = conflictIdSet.has(apt.id);
                      const isDragging = draggingId === apt.id;
                      return (
                        <div
                          key={apt.id}
                          data-testid={`appointment-card-${apt.id}`}
                          draggable
                          onDragStart={(e) => onDragStart(apt, e)}
                          onDragEnd={onDragEnd}
                          style={{
                            padding: '0.75rem',
                            background: service?.color || '#0073ea',
                            borderRadius: '6px',
                            borderLeft: crewColor ? `5px solid ${crewColor}` : undefined,
                            outline: isConflict ? '2px solid #dc2626' : 'none',
                            outlineOffset: isConflict ? '1px' : 0,
                            color: '#fff',
                            fontSize: '0.75rem',
                            cursor: isEmployee ? 'default' : 'grab',
                            opacity: isDragging ? 0.4 : 1,
                            transition: 'transform 0.15s, opacity 0.15s',
                          }}
                          onMouseEnter={(e) => !isDragging && (e.currentTarget.style.transform = 'scale(1.02)')}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isEmployee) return;
                            handleEdit(apt);
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
                            <Clock size={12} />
                            <span style={{ fontWeight: 600 }}>{apt.startTime}</span>
                            {isConflict && (
                              <span
                                data-testid={`appointment-conflict-${apt.id}`}
                                title="Overlaps another appointment for the same employee"
                                style={{
                                  marginLeft: 4, display: 'inline-flex', alignItems: 'center', gap: 2,
                                  padding: '1px 5px', borderRadius: 4,
                                  background: '#fee2e2', color: '#b91c1c',
                                  fontSize: '0.65rem', fontWeight: 700,
                                }}
                              >
                                <AlertTriangle size={10} /> Conflict
                              </span>
                            )}
                            {apt.recurringGroup && <Repeat size={12} style={{ marginLeft: 'auto' }} />}
                          </div>
                          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{apt.customerName}</div>
                          <div style={{ opacity: 0.9 }}>{service?.label}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              data-testid={`appointment-status-${apt.id}`}
                              onClick={(e) => cycleStatus(apt, e)}
                              title="Click to advance status"
                              style={{
                                padding: '2px 8px', borderRadius: 999, border: 'none',
                                background: statusStyle.bg,
                                color: statusStyle.fg || '#fff',
                                fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer',
                              }}
                            >{statusStyle.label}</button>
                            <select
                              data-testid={`appointment-reassign-${apt.id}`}
                              value=""
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => quickReassign(apt, e.target.value, e)}
                              title="Quick reassign"
                              style={{
                                padding: '1px 4px', borderRadius: 4, border: 'none',
                                background: 'rgba(255,255,255,0.22)', color: '#fff',
                                fontSize: '0.65rem', cursor: 'pointer', maxWidth: 90,
                              }}
                            >
                              <option value="">{apt.assignedEmployees.length} assigned</option>
                              {employees.map((e) => (
                                <option key={e.id} value={e.id} style={{ color: '#000' }}>
                                  {apt.assignedEmployees.includes(e.id) ? '✓ ' : ''}{e.name}
                                </option>
                              ))}
                            </select>
                            {aptCrewId && (
                              <span
                                data-testid={`appointment-crew-${apt.id}`}
                                style={{
                                  marginLeft: 'auto',
                                  padding: '0.05rem 0.4rem',
                                  borderRadius: 999,
                                  background: 'rgba(255,255,255,0.22)',
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                }}
                              >
                                {getCrewName(aptCrewId)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reschedule popup — opens after dropping a card on a new day */}
      {pendingMove && (
        <div
          data-testid="reschedule-popup"
          onClick={() => setPendingMove(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100, padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 14, width: '100%', maxWidth: 380,
              padding: '1.5rem 1.5rem 1.25rem',
              boxShadow: '0 20px 60px rgba(15,23,42,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>
                Reschedule appointment
              </h3>
              <button
                type="button"
                data-testid="reschedule-popup-close"
                onClick={() => setPendingMove(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: 14 }}>
              <strong style={{ color: '#0f172a' }}>{pendingMove.apt.customerName}</strong>
              <span style={{ color: '#94a3b8' }}> · was </span>
              {new Date(pendingMove.apt.date).toLocaleDateString()} {pendingMove.apt.startTime}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#475569', fontWeight: 600 }}>
                New date
                <input
                  type="date"
                  data-testid="reschedule-date-input"
                  value={pendingMove.dateStr}
                  onChange={(e) => setPendingMove((p) => ({ ...p, dateStr: e.target.value }))}
                  style={{
                    padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
                    fontSize: 14, color: '#0f172a',
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#475569', fontWeight: 600 }}>
                Start time
                <input
                  type="time"
                  data-testid="reschedule-time-input"
                  value={pendingMove.startTime}
                  onChange={(e) => setPendingMove((p) => ({ ...p, startTime: e.target.value }))}
                  style={{
                    padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
                    fontSize: 14, color: '#0f172a',
                  }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                data-testid="reschedule-cancel"
                onClick={() => setPendingMove(null)}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0',
                  background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                type="button"
                data-testid="reschedule-confirm"
                onClick={confirmPendingMove}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: '#0073ea', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }}
              >Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Appointment Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              position: 'sticky',
              top: 0,
              background: '#fff',
              zIndex: 1
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {editingAppointment ? 'Edit Appointment' : 'New Appointment'}
              </h2>
              <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              {/* Customer Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Customer *</label>
                <select
                  value={formData.customerId}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  required
                  className="input"
                >
                  <option value="">Select customer...</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </div>

              {/* Service Type */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Service Type *</label>
                <select
                  value={formData.serviceType}
                  onChange={(e) => {
                    const service = serviceTypes.find(s => s.value === e.target.value);
                    setFormData({ ...formData, serviceType: e.target.value, duration: service?.duration || 2 });
                  }}
                  required
                  className="input"
                >
                  {serviceTypes.map(service => (
                    <option key={service.value} value={service.value}>
                      {service.label} ({service.duration} hours)
                    </option>
                  ))}
                </select>
              </div>

              {/* Date & Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Start Time *</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Duration (hours)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseFloat(e.target.value) })}
                    className="input"
                  />
                </div>
              </div>

              {/* Assign Employees */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.75rem' }}>Assign Employees *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  {employees.map(emp => (
                    <label key={emp.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem',
                      border: formData.assignedEmployees.includes(emp.id) ? '2px solid #0073ea' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: formData.assignedEmployees.includes(emp.id) ? '#f0f9ff' : '#fff',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.assignedEmployees.includes(emp.id)}
                        onChange={() => toggleEmployee(emp.id)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.875rem', fontWeight: formData.assignedEmployees.includes(emp.id) ? 600 : 400 }}>
                        {emp.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Recurring Options */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.recurring}
                    onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <Repeat size={16} />
                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Make this a recurring appointment</span>
                </label>

                {formData.recurring && (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Frequency *</label>
                      <select
                        value={formData.recurringFrequency}
                        onChange={(e) => setFormData({ ...formData, recurringFrequency: e.target.value })}
                        className="input"
                      >
                        {recurringOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>End Date *</label>
                      <input
                        type="date"
                        value={formData.recurringEndDate}
                        onChange={(e) => setFormData({ ...formData, recurringEndDate: e.target.value })}
                        min={formData.date}
                        required={formData.recurring}
                        className="input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  style={{ minHeight: '80px' }}
                  placeholder="Add any special instructions..."
                />
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between' }}>
                {editingAppointment && (
                  <button
                    type="button"
                    onClick={() => handleDelete(editingAppointment.id)}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
                  <button type="button" onClick={resetForm} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingAppointment ? 'Update' : 'Create'} Appointment
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Print/Email Modal */}
      {showPrintModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              position: 'sticky',
              top: 0,
              background: '#fff',
              zIndex: 1
            }} className="no-print">
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Print/Email Weekly Schedule</h2>
              <button 
                onClick={() => setShowPrintModal(false)} 
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Filter Options */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }} className="no-print">
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Schedule View
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => setPrintView('weekly')}
                    className={printView === 'weekly' ? 'btn btn-primary' : 'btn btn-secondary'}
                    style={{ flex: 1 }}
                  >
                    Weekly Schedule
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrintView('daily')}
                    className={printView === 'daily' ? 'btn btn-primary' : 'btn btn-secondary'}
                    style={{ flex: 1 }}
                  >
                    Daily Schedule
                  </button>
                </div>
              </div>

              {printView === 'daily' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={selectedPrintDate}
                    onChange={(e) => setSelectedPrintDate(e.target.value)}
                    className="input"
                  />
                </div>
              )}
              
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                Filter by Employee
              </label>
              <select
                value={printFilter}
                onChange={(e) => setPrintFilter(e.target.value)}
                className="input"
                style={{ marginBottom: '1rem' }}
              >
                <option value="all">All Employees - Full Schedule</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}'s Schedule</option>
                ))}
              </select>
              
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={handlePrint} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Printer size={18} />
                  Print Schedule
                </button>
                <button onClick={handleEmail} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Mail size={18} />
                  Email Schedule
                </button>
              </div>
            </div>

            {/* Printable Schedule */}
            <div style={{ padding: '2rem' }} id="printable-schedule">
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                  Maidly Business Manager
                </h1>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
                  {printView === 'daily' ? 'Daily Schedule' : 'Weekly Schedule'}
                </h2>
                <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                  {printView === 'daily' 
                    ? new Date(selectedPrintDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                    : `${weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                  }
                </p>
                {printFilter !== 'all' && (
                  <p style={{ color: '#0073ea', fontSize: '0.875rem', fontWeight: 600, marginTop: '0.5rem' }}>
                    Employee: {employees.find(e => e.id === printFilter)?.name}
                  </p>
                )}
              </div>

              {printView === 'daily' ? (
                // Daily Schedule View
                <div>
                  {(() => {
                    const filteredAppts = getFilteredAppointments().filter(apt => apt.date === selectedPrintDate);
                    const sortedAppts = filteredAppts.sort((a, b) => a.startTime.localeCompare(b.startTime));
                    
                    return (
                      <div style={{ marginBottom: '2rem' }}>
                        <div style={{
                          background: '#f3f4f6',
                          padding: '0.75rem 1rem',
                          borderRadius: '6px',
                          marginBottom: '0.75rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>
                            {new Date(selectedPrintDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                          </h3>
                          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {sortedAppts.length} appointment{sortedAppts.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {sortedAppts.length === 0 ? (
                          <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem', fontSize: '0.875rem' }}>
                            No appointments scheduled for this day
                          </p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {sortedAppts.map(apt => {
                              const service = serviceTypes.find(s => s.value === apt.serviceType);
                              const assignedNames = employees.filter(e => apt.assignedEmployees.includes(e.id)).map(e => e.name).join(', ');
                              
                              return (
                                <div key={apt.id} style={{
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  padding: '1rem',
                                  background: '#fff'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <Clock size={16} color="#6b7280" />
                                      <span style={{ fontSize: '1rem', fontWeight: 600 }}>
                                        {apt.startTime}
                                      </span>
                                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                        ({apt.duration} hours)
                                      </span>
                                      {apt.recurringGroup && (
                                        <span style={{
                                          background: '#dbeafe',
                                          color: '#1e40af',
                                          padding: '0.125rem 0.5rem',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}>
                                          RECURRING
                                        </span>
                                      )}
                                    </div>
                                    <span style={{
                                      fontSize: '0.875rem',
                                      fontWeight: 600,
                                      color: service?.color || '#0073ea'
                                    }}>
                                      {service?.label}
                                    </span>
                                  </div>
                                  
                                  <div style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                    {apt.customerName}
                                  </div>
                                  
                                  <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    <MapPin size={14} color="#6b7280" style={{ marginTop: '2px' }} />
                                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                      {apt.address}
                                    </span>
                                  </div>
                                  
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <User size={14} color="#6b7280" />
                                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                      <strong>Assigned:</strong> {assignedNames}
                                    </span>
                                  </div>
                                  
                                  {apt.notes && (
                                    <div style={{
                                      marginTop: '0.75rem',
                                      padding: '0.75rem',
                                      background: '#fffbeb',
                                      borderRadius: '4px',
                                      fontSize: '0.875rem',
                                      color: '#92400e'
                                    }}>
                                      <strong>Notes:</strong> {apt.notes}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                // Weekly Schedule View
                weekDays.map((day) => {
                  const filteredAppts = getFilteredAppointments().filter(apt => apt.date === day.toISOString().split('T')[0]);
                  const sortedAppts = filteredAppts.sort((a, b) => a.startTime.localeCompare(b.startTime));
                  
                  return (
                    <div key={day.toISOString().split('T')[0]} style={{ marginBottom: '2rem', pageBreakInside: 'avoid' }}>
                      <div style={{
                        background: '#f3f4f6',
                        padding: '0.75rem 1rem',
                        borderRadius: '6px',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>
                          {day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </h3>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {sortedAppts.length} appointment{sortedAppts.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {sortedAppts.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#9ca3af', padding: '1rem', fontSize: '0.875rem' }}>
                          No appointments scheduled
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {sortedAppts.map(apt => {
                            const service = serviceTypes.find(s => s.value === apt.serviceType);
                            const assignedNames = employees.filter(e => apt.assignedEmployees.includes(e.id)).map(e => e.name).join(', ');
                            
                            return (
                              <div key={apt.id} style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                padding: '1rem',
                                background: '#fff'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Clock size={16} color="#6b7280" />
                                    <span style={{ fontSize: '1rem', fontWeight: 600 }}>
                                      {apt.startTime}
                                    </span>
                                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                      ({apt.duration} hours)
                                    </span>
                                    {apt.recurringGroup && (
                                      <span style={{
                                        background: '#dbeafe',
                                        color: '#1e40af',
                                        padding: '0.125rem 0.5rem',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}>
                                        RECURRING
                                      </span>
                                    )}
                                  </div>
                                  <span style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: service?.color || '#0073ea'
                                  }}>
                                    {service?.label}
                                  </span>
                                </div>
                                
                                <div style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                  {apt.customerName}
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  <MapPin size={14} color="#6b7280" style={{ marginTop: '2px' }} />
                                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                    {apt.address}
                                  </span>
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <User size={14} color="#6b7280" />
                                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                    <strong>Assigned:</strong> {assignedNames}
                                  </span>
                                </div>
                                
                                {apt.notes && (
                                  <div style={{
                                    marginTop: '0.75rem',
                                    padding: '0.75rem',
                                    background: '#fffbeb',
                                    borderRadius: '4px',
                                    fontSize: '0.875rem',
                                    color: '#92400e'
                                  }}>
                                    <strong>Notes:</strong> {apt.notes}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Content Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              position: 'sticky',
              top: 0,
              background: '#fff',
              zIndex: 1
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Email Schedule</h2>
              <button 
                onClick={() => setShowEmailModal(false)} 
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <div style={{ 
                padding: '1rem', 
                background: '#f0f9ff', 
                borderRadius: '6px', 
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                color: '#1e40af'
              }}>
                <strong>📧 How to use:</strong> Copy the content below and paste it into your email client, or click "Open in Email" to try the default mailto link.
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Subject:
                </label>
                <div style={{ 
                  padding: '0.75rem', 
                  background: '#f9fafb', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}>
                  {emailContent.subject}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Message:
                </label>
                <textarea
                  value={emailContent.body}
                  readOnly
                  style={{ 
                    width: '100%',
                    minHeight: '300px',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button 
                  onClick={copyEmailContent}
                  className="btn btn-primary"
                  style={{ flex: 1, minWidth: '150px' }}
                >
                  <Download size={18} style={{ marginRight: '0.5rem' }} />
                  Copy to Clipboard
                </button>
                <button 
                  onClick={openInEmailClient}
                  className="btn btn-secondary"
                  style={{ flex: 1, minWidth: '150px' }}
                >
                  <Mail size={18} style={{ marginRight: '0.5rem' }} />
                  Open in Email
                </button>
                <button 
                  onClick={() => setShowEmailModal(false)}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scheduling;
